// ── MULTI-DEVICE SESSION MANAGER ─────────────────────────────────────────────
// SEC-14: Device ID moved from localStorage to sessionStorage (privacy)
// SEC-07: Session timeout tracking integrated
//
// PLAINTEXT BY DESIGN
// ─────────────────────
// `sykes-active-sessions` is read by the launcher (App.jsx → ActiveSessionsBadge)
// to show a "X devices active" indicator BEFORE any user has logged in. That
// badge therefore needs synchronous, unencrypted access — Encryption.init only
// runs at login time, when the JWT-derived passphrase becomes available.
//
// To make plaintext storage acceptable, the session record is deliberately
// minimal: portal name, random per-device ID (sessionStorage-scoped, regenerated
// on browser-tab close), an isMobile boolean, and timestamps. Crucially we do
// NOT store userId, username, full userAgent, or any other identifier that
// could correlate the session to a specific person via filesystem inspection.
// An attacker with localStorage access would learn only: "some unidentified
// browser was active in <portal> at <time>" — which they could already infer
// from the existence of the launcher itself.
//
// FOLLOWUP-#2 (26 Apr 2026): tightened from prior schema. Previously we
// stored userId (the integer DB row id) and a 50-char userAgent prefix.
// Both have been removed; the badge UI was checked to confirm only deviceId,
// id, lastActivity, and isMobile are read.

const SessionManager = {
  _deviceId: null,
  getDeviceId() {
    if (this._deviceId) return this._deviceId;
    try {
      // SEC-14: sessionStorage instead of localStorage — clears on tab close
      let id = sessionStorage.getItem('sykes-device-id');
      if (!id) {
        id = `DEV-${Date.now()}-${Math.random().toString(36).substr(2,8)}`;
        sessionStorage.setItem('sykes-device-id', id);
      }
      this._deviceId = id;
    } catch { this._deviceId = `DEV-${Date.now()}-${Math.random().toString(36).substr(2,8)}`; }
    return this._deviceId;
  },
  async createSession(portal, _userId /* deprecated, ignored */) {
    const session = {
      id: `SES-${Date.now()}-${Math.random().toString(36).substr(2,6)}`,
      portal,
      deviceId: this.getDeviceId(),
      // FOLLOWUP-#2: a single boolean derived from the user-agent string,
      // not the string itself. The launcher only needs to label devices as
      // Mobile vs Desktop; it never needed the other 47 chars.
      isMobile: /Mobile|Android|iPhone|iPad/.test(navigator.userAgent || ''),
      createdAt: new Date().toISOString(),
      lastActivity: new Date().toISOString(),
    };
    try {
      if (window.storage) {
        const existing = await this._getSessions();
        existing.push(session);
        await window.storage.set('sykes-active-sessions', JSON.stringify(existing));
      }
    } catch (e) { /* session create failed silently */ }
    return session;
  },
  async updateActivity(sessionId) {
    try {
      if (!window.storage) return;
      const sessions = await this._getSessions();
      const updated = sessions.map(s => s.id === sessionId ? { ...s, lastActivity: new Date().toISOString() } : s);
      await window.storage.set('sykes-active-sessions', JSON.stringify(updated));
    } catch (e) { /* session update failed silently */ }
  },
  async endSession(sessionId) {
    try {
      if (!window.storage) return;
      const sessions = await this._getSessions();
      await window.storage.set('sykes-active-sessions', JSON.stringify(sessions.filter(s => s.id !== sessionId)));
    } catch (e) { /* session end failed silently */ }
  },
  async getActiveSessions(portal) {
    const sessions = await this._getSessions();
    const cutoff = Date.now() - 30 * 60 * 1000; // 30 min timeout
    return sessions.filter(s => (!portal || s.portal === portal) && new Date(s.lastActivity).getTime() > cutoff);
  },
  async _getSessions() {
    try {
      if (!window.storage) return [];
      const result = await window.storage.get('sykes-active-sessions');
      return result && result.value ? JSON.parse(result.value) : [];
    } catch { return []; }
  }
};

// FOLLOWUP-#2: one-time cleanup of legacy session records that still carry
// userId or a full userAgent string. Pre-existing entries written by the old
// schema would otherwise sit in localStorage indefinitely until they age out
// of the 30-minute lastActivity cutoff. We rewrite each entry to the new
// minimal shape so the on-disk footprint matches the current code path.
// Idempotent and a no-op when no legacy entries are present.
(() => {
  try {
    if (typeof localStorage === 'undefined') return;
    const raw = localStorage.getItem('sykes-active-sessions');
    if (!raw) return;
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr) || arr.length === 0) return;
    let dirty = false;
    const cleaned = arr.map(s => {
      if (s == null || typeof s !== 'object') return s;
      const hasLegacy = ('userId' in s) || ('userAgent' in s);
      if (!hasLegacy) return s;
      dirty = true;
      const { userId, userAgent, ...rest } = s;
      return {
        ...rest,
        isMobile: rest.isMobile ?? /Mobile|Android|iPhone|iPad/.test(typeof userAgent === 'string' ? userAgent : ''),
      };
    });
    if (dirty) {
      localStorage.setItem('sykes-active-sessions', JSON.stringify(cleaned));
      console.log('[SessionManager] Cleaned legacy session record fields (FOLLOWUP-#2)');
    }
  } catch (_) { /* malformed data — leave alone, getActiveSessions will return [] */ }
})();

export { SessionManager };
